import axiosConfig from "./axiosConfig";

// Cache simple pour les requêtes GET
const cache = new Map();
const CACHE_DURATION = 5 * 60 * 1000;

const documentService = {
  /**
   * Crée un nouveau document associé à un article
   * @param {FormData} formData Les données du document à créer
   * @returns {Promise} La réponse de l'API
   */
  createDocumentArticle: (formData) => {
    return axiosConfig.post(
      '/v1/document_articles',
      formData,
      {
        headers: {
          "Content-Type": "multipart/form-data",
        },
      }
    ).then(response => response.data);
  },

  /**
   * Récupère tous les documents associés à un article
   * @param {number} articleId L'ID de l'article
   * @param {boolean} useCache Utiliser le cache si disponible (par défaut: true)
   * @returns {Promise} La réponse de l'API
   */
  getArticleDocuments: (articleId, useCache = true) => {
    const cacheKey = `article-docs-${articleId}`;
    
    // Vérifier si les données sont dans le cache et toujours valides
    if (useCache && cache.has(cacheKey)) {
      const cachedData = cache.get(cacheKey);
      if (Date.now() - cachedData.timestamp < CACHE_DURATION) {
        return Promise.resolve(cachedData.data);
      }
    }
    
    // Sinon faire l'appel API
    return axiosConfig.get(`/v1/document_articles/article/${articleId}`)
      .then(response => {
        const data = response.data;
        // Mettre en cache les données
        if (useCache) {
          cache.set(cacheKey, {
            data,
            timestamp: Date.now()
          });
        }
        return data;
      });
  },

  /**
   * Supprime un document
   * @param {number} documentId L'ID du document à supprimer
   * @returns {Promise} La réponse de l'API
   */
  deleteDocument: (documentId) => {
    return axiosConfig.delete(`/v1/document_articles/${documentId}`)
      .then(response => response.data);
  },

  /**
   * Met à jour un document existant
   * @param {number} documentId L'ID du document à mettre à jour
   * @param {FormData} formData Les nouvelles données du document
   * @returns {Promise} La réponse de l'API
   */
  updateDocument: (documentId, formData) => {
    return axiosConfig.put(
      `/v1/document_articles/${documentId}`,
      formData,
      {
        headers: {
          "Content-Type": "multipart/form-data",
        },
      }
    ).then(response => response.data);
  },

  /**
   * Crée plusieurs documents en parallèle
   * @param {Array<FormData>} formDataArray Tableau de FormData pour plusieurs documents
   * @returns {Promise<Array>} Tableau des réponses dans l'ordre des requêtes
   */
  createMultipleDocuments: (formDataArray) => {
    const promises = formDataArray.map(formData => 
      documentService.createDocumentArticle(formData)
    );
    return Promise.all(promises);
  },

  /**
   * Supprime plusieurs documents en parallèle
   * @param {Array<number>} documentIds Tableau d'IDs de documents à supprimer
   * @returns {Promise<Array>} Tableau des réponses dans l'ordre des requêtes
   */
  deleteMultipleDocuments: (documentIds) => {
    const promises = documentIds.map(id => 
      documentService.deleteDocument(id)
    );
    return Promise.all(promises);
  },

  /**
   * Invalide le cache pour un article spécifique
   * @param {number} articleId L'ID de l'article
   */
  invalidateCache: (articleId) => {
    const cacheKey = `article-docs-${articleId}`;
    cache.delete(cacheKey);
  }
};

export default documentService;